import { Box, Group, Select, Stack, TextInput } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
	apiQueryKey,
	useDeleteAutomationLlmProvider,
	useDeleteEfficientLlmProvider,
	useDeleteIntelligentLlmProvider,
	useGetAutomationLlmProvider,
	useGetEfficientLlmProvider,
	useGetIntelligentLlmProvider,
	useSetAutomationLlmProvider,
	useSetEfficientLlmProvider,
	useSetIntelligentLlmProvider,
} from '@repo/api-codegen';
import { SettingsButton } from '@repo/common/components';
import { Button, ResourceIcon, Text } from '@repo/foundations';
import { useCallback, useState } from 'react';
import { queryClient } from '../../../api';
import { closeAllModals, openModal } from '../../ModalManager';

const formStyles = {
	formGrid: {
		display: 'grid',
		gridTemplateColumns: '1fr',
		gap: '12px',
	},
	formActions: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: '12px',
		marginTop: '16px',
	},
};

interface LLMProviderFormData {
	compatible_client: 'OpenAI' | 'VertexAI';
	model_name: string;
	host: string;
	api_key: string;
}

interface LLMProviderFormProps {
	initialData?: LLMProviderFormData;
	onSubmit: (data: LLMProviderFormData) => Promise<void>;
	onCancel: () => void;
	isLoading: boolean;
}

function LLMProviderForm({
	initialData,
	onSubmit,
	onCancel,
	isLoading,
}: LLMProviderFormProps) {
	const [formData, setFormData] = useState<LLMProviderFormData>({
		compatible_client: initialData?.compatible_client || 'VertexAI',
		model_name: initialData?.model_name || '',
		host: initialData?.host || '',
		api_key: initialData?.api_key || '',
	});

	const [errors, setErrors] = useState<Record<string, string>>({});

	const updateField = (field: keyof LLMProviderFormData) => (value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));

		// Clear error when user starts typing
		if (errors[field]) {
			setErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors[field];
				return newErrors;
			});
		}
	};

	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.model_name.trim()) {
			newErrors.model_name = 'Model name is required';
		}

		if (!formData.api_key.trim()) {
			newErrors.api_key = 'API key is required';
		}

		if (formData.compatible_client === 'OpenAI' && !formData.host.trim()) {
			newErrors.host = 'Host is required';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async () => {
		if (!validateForm()) return;

		try {
			await onSubmit(formData);
		} catch {
			// Error handling is done in the parent component
		}
	};

	return (
		<Stack spacing="md">
			<div style={formStyles.formGrid}>
				<Stack spacing="2xs">
					<Text size="sm" weight="semibold">
						Compatible Client
					</Text>
					<Text size="xs" color="text/secondary/default" mb="2xs">
						The client interface to use
					</Text>
					<Select
						value={formData.compatible_client}
						onChange={(value: string | null) =>
							updateField('compatible_client')(value || 'VertexAI')
						}
						data={[
							{ value: 'OpenAI', label: 'OpenAI-Compatible API' },
							{ value: 'VertexAI', label: 'Google Vertex AI' },
						]}
						variant="filled"
					/>
				</Stack>

				<Stack spacing="2xs">
					<Text size="sm" weight="semibold">
						Model Name
					</Text>
					<Text size="xs" color="text/secondary/default" mb="2xs">
						The specific model to use (for example: gemini-3.1-pro-preview,
						claude-sonnet-4-5, grok-4)
					</Text>
					<TextInput
						value={formData.model_name}
						onChange={(e) => updateField('model_name')(e.target.value)}
						placeholder="gemini-3.1-pro-preview"
						error={errors.model_name}
						variant="filled"
					/>
				</Stack>

				{formData.compatible_client === 'OpenAI' && (
					<Stack spacing="2xs">
						<Text size="sm" weight="semibold">
							Host
						</Text>
						<Text size="xs" color="text/secondary/default" mb="2xs">
							The API endpoint URL
						</Text>
						<TextInput
							value={formData.host}
							onChange={(e) => updateField('host')(e.target.value)}
							placeholder="https://api.provider.com/v1"
							error={errors.host}
							variant="filled"
						/>
					</Stack>
				)}

				<Stack spacing="2xs">
					<Text size="sm" weight="semibold">
						API Key
					</Text>
					<Text size="xs" color="text/secondary/default" mb="2xs">
						API key for the selected provider
					</Text>
					<TextInput
						value={formData.api_key}
						onChange={(e) => updateField('api_key')(e.target.value)}
						placeholder="Provider API key"
						error={errors.api_key}
						variant="filled"
						type="password"
					/>
				</Stack>
			</div>

			<div style={formStyles.formActions}>
				<Button variant="tertiary" onClick={onCancel} disabled={isLoading}>
					Cancel
				</Button>
				<Button variant="primary" onClick={handleSubmit} loading={isLoading}>
					Save Configuration
				</Button>
			</div>
		</Stack>
	);
}

export function LLMProviderSettings() {
	// Intelligent LLM hooks
	const { data: intelligentLlm } = useGetIntelligentLlmProvider({});
	const { mutateAsync: setIntelligentLlm, isPending: isSettingIntelligent } =
		useSetIntelligentLlmProvider({});
	const { mutateAsync: deleteIntelligentLlm } = useDeleteIntelligentLlmProvider(
		{}
	);

	// Efficient LLM hooks
	const { data: efficientLlm } = useGetEfficientLlmProvider({});
	const { mutateAsync: setEfficientLlm, isPending: isSettingEfficient } =
		useSetEfficientLlmProvider({});
	const { mutateAsync: deleteEfficientLlm } = useDeleteEfficientLlmProvider({});

	// Automation LLM hooks
	const { data: automationLlm } = useGetAutomationLlmProvider({});
	const { mutateAsync: setAutomationLlm, isPending: isSettingAutomation } =
		useSetAutomationLlmProvider({});
	const { mutateAsync: deleteAutomationLlm } = useDeleteAutomationLlmProvider(
		{}
	);

	const handleConfigureProvider = useCallback(
		(
			providerType: 'intelligent' | 'efficient' | 'automation',
			initialData?: LLMProviderFormData
		) => {
			const titles = {
				intelligent: 'Configure Intelligent LLM Provider',
				efficient: 'Configure Efficient LLM Provider',
				automation: 'Configure Automation LLM Provider',
			};

			const mutators = {
				intelligent: setIntelligentLlm,
				efficient: setEfficientLlm,
				automation: setAutomationLlm,
			};

			const loadingStates = {
				intelligent: isSettingIntelligent,
				efficient: isSettingEfficient,
				automation: isSettingAutomation,
			};

			openModal({
				title: titles[providerType],
				size: 'lg',
				children: (
					<LLMProviderForm
						initialData={initialData}
						onSubmit={async (data) => {
							try {
								await mutators[providerType]({
									body: {
										compatible_client: data.compatible_client,
										model_name: data.model_name,
										host:
											data.compatible_client === 'VertexAI'
												? 'https://vertex-ai.googleapis.com'
												: data.host,
										api_key: data.api_key,
									},
								});

								// Invalidate the query cache to refresh the data
								const paths = {
									intelligent: 'ai/preferences/intelligent-llm' as const,
									efficient: 'ai/preferences/efficient-llm' as const,
									automation: 'ai/preferences/automation-llm' as const,
								};

								queryClient.invalidateQueries({
									queryKey: apiQueryKey(paths[providerType]),
								});

								showNotification({
									title: 'Success',
									message: `${providerType} LLM provider configured successfully`,
									color: 'green',
								});
								closeAllModals();
							} catch (error) {
								showNotification({
									title: 'Error',
									message: `Failed to configure ${providerType} LLM provider`,
									color: 'red',
								});
								throw error;
							}
						}}
						onCancel={() => closeAllModals()}
						isLoading={loadingStates[providerType]}
					/>
				),
			});
		},
		[
			setIntelligentLlm,
			setEfficientLlm,
			setAutomationLlm,
			isSettingIntelligent,
			isSettingEfficient,
			isSettingAutomation,
		]
	);

	const handleDeleteProvider = useCallback(
		(providerType: 'intelligent' | 'efficient' | 'automation') => {
			const deletors = {
				intelligent: deleteIntelligentLlm,
				efficient: deleteEfficientLlm,
				automation: deleteAutomationLlm,
			};

			openModal({
				title: 'Confirm Deletion',
				children: (
					<Stack spacing="md">
						<Text>
							Are you sure you want to remove this LLM provider configuration?
							This action cannot be undone.
						</Text>
						<Group position="right" spacing="sm">
							<Button variant="tertiary" onClick={() => closeAllModals()}>
								Cancel
							</Button>
							<Button
								variant="default"
								tone="critical"
								onClick={async () => {
									try {
										await deletors[providerType]({});

										// Invalidate the query cache to refresh the data
										const paths = {
											intelligent: 'ai/preferences/intelligent-llm' as const,
											efficient: 'ai/preferences/efficient-llm' as const,
											automation: 'ai/preferences/automation-llm' as const,
										};

										queryClient.invalidateQueries({
											queryKey: apiQueryKey(paths[providerType]),
										});

										showNotification({
											title: 'Success',
											message: `${providerType} LLM provider removed successfully`,
											color: 'green',
										});
										closeAllModals();
									} catch {
										showNotification({
											title: 'Error',
											message: `Failed to remove ${providerType} LLM provider`,
											color: 'red',
										});
									}
								}}
							>
								Remove
							</Button>
						</Group>
					</Stack>
				),
			});
		},
		[deleteIntelligentLlm, deleteEfficientLlm, deleteAutomationLlm]
	);

	// Helper function to create description for each provider
	const getProviderDescription = (
		baseDescription: string,
		config?: { model_name?: string | null },
		configured?: boolean
	) => {
		if (!configured || !config?.model_name) {
			return baseDescription;
		}
		return `${baseDescription} • Currently using: ${config.model_name}`;
	};

	const isIntelligentConfigured = !!intelligentLlm?.model_name;
	const isEfficientConfigured = !!efficientLlm?.model_name;
	const isAutomationConfigured = !!automationLlm?.model_name;

	return (
		<Box>
			<SettingsButton
				title="Intelligent LLM"
				description={getProviderDescription(
					'Used for complex reasoning tasks and detailed analysis. This is used by the agents in general. We recommend a model with 128k tokens or more. Must support function calling.',
					intelligentLlm,
					isIntelligentConfigured
				)}
				leftComponent={<ResourceIcon iconName="desktop" />}
				buttonText={isIntelligentConfigured ? 'Edit' : 'Configure'}
				onClick={() =>
					handleConfigureProvider(
						'intelligent',
						intelligentLlm
							? {
									compatible_client:
										(intelligentLlm.compatible_client as
											| 'OpenAI'
											| 'VertexAI') || 'VertexAI',
									model_name: intelligentLlm.model_name || '',
									host: intelligentLlm.host || '',
									api_key: intelligentLlm.api_key || '',
								}
							: undefined
					)
				}
				onClickRow={() =>
					handleConfigureProvider(
						'intelligent',
						intelligentLlm
							? {
									compatible_client:
										(intelligentLlm.compatible_client as
											| 'OpenAI'
											| 'VertexAI') || 'VertexAI',
									model_name: intelligentLlm.model_name || '',
									host: intelligentLlm.host || '',
									api_key: intelligentLlm.api_key || '',
								}
							: undefined
					)
				}
				rightComponent={
					isIntelligentConfigured ? (
						<Button
							variant="tertiary"
							size="sm"
							onClick={(e) => {
								e.stopPropagation();
								handleDeleteProvider('intelligent');
							}}
						>
							Remove
						</Button>
					) : undefined
				}
			/>

			<SettingsButton
				title="High-Performance LLM"
				description={getProviderDescription(
					'Used for tasks that require fast processing with a high context window. We recommend a model with 1M tokens or more. Must support function calling.',
					efficientLlm,
					isEfficientConfigured
				)}
				leftComponent={<ResourceIcon iconName="briefcase" />}
				buttonText={isEfficientConfigured ? 'Edit' : 'Configure'}
				onClick={() =>
					handleConfigureProvider(
						'efficient',
						efficientLlm
							? {
									compatible_client:
										(efficientLlm.compatible_client as 'OpenAI' | 'VertexAI') ||
										'VertexAI',
									model_name: efficientLlm.model_name || '',
									host: efficientLlm.host || '',
									api_key: efficientLlm.api_key || '',
								}
							: undefined
					)
				}
				onClickRow={() =>
					handleConfigureProvider(
						'efficient',
						efficientLlm
							? {
									compatible_client:
										(efficientLlm.compatible_client as 'OpenAI' | 'VertexAI') ||
										'VertexAI',
									model_name: efficientLlm.model_name || '',
									host: efficientLlm.host || '',
									api_key: efficientLlm.api_key || '',
								}
							: undefined
					)
				}
				rightComponent={
					isEfficientConfigured ? (
						<Button
							variant="tertiary"
							size="sm"
							onClick={(e) => {
								e.stopPropagation();
								handleDeleteProvider('efficient');
							}}
						>
							Remove
						</Button>
					) : undefined
				}
			/>

			<SettingsButton
				title="Automation LLM provider"
				description={getProviderDescription(
					'Used for automated tasks and background operations. We recommend a model with 1M tokens or more. This model should be inexpensive to run and fast to respond. Must support function calling.',
					automationLlm,
					isAutomationConfigured
				)}
				leftComponent={<ResourceIcon iconName="settings" />}
				buttonText={isAutomationConfigured ? 'Edit' : 'Configure'}
				onClick={() =>
					handleConfigureProvider(
						'automation',
						automationLlm
							? {
									compatible_client:
										(automationLlm.compatible_client as
											| 'OpenAI'
											| 'VertexAI') || 'VertexAI',
									model_name: automationLlm.model_name || '',
									host: automationLlm.host || '',
									api_key: automationLlm.api_key || '',
								}
							: undefined
					)
				}
				onClickRow={() =>
					handleConfigureProvider(
						'automation',
						automationLlm
							? {
									compatible_client:
										(automationLlm.compatible_client as
											| 'OpenAI'
											| 'VertexAI') || 'VertexAI',
									model_name: automationLlm.model_name || '',
									host: automationLlm.host || '',
									api_key: automationLlm.api_key || '',
								}
							: undefined
					)
				}
				rightComponent={
					isAutomationConfigured ? (
						<Button
							variant="tertiary"
							size="sm"
							onClick={(e) => {
								e.stopPropagation();
								handleDeleteProvider('automation');
							}}
						>
							Remove
						</Button>
					) : undefined
				}
			/>
		</Box>
	);
}
